import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload, LogOut, Save, Images, X, Film } from "lucide-react";
import { api, setToken, clearToken, getToken, uploadImage, resolveImg, getMedia } from "./api";
import { useContent, DEFAULT_SETTINGS, DEFAULT_MENU } from "./ContentContext";
import { TEXT_SCHEMA, SIZE_OPTS, WEIGHT_OPTS, DEFAULT_TEXT } from "./textConfig";
import { PAGE_OPTIONS, SECTION_OPTIONS, ANIMATION_OPTIONS } from "./PopupAds";
import { THEME_COLORS, CHROME_COLORS, DEFAULT_THEME, applyTheme } from "./theme";
import { Seo } from "./Seo";

const clone = (o) => JSON.parse(JSON.stringify(o));

const isVideo = (u = "") => /\.(mp4|mov|webm|ogg)(\?|$)/i.test(u);

const MediaPicker = ({ open, onClose, onSelect, kind = "image" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getMedia()
      .then((list) => setItems(list))
      .catch(() => toast.error("Could not load media"))
      .finally(() => setLoading(false));
  }, [open]);
  if (!open) return null;
  const shown = items.filter((it) => {
    const v = isVideo(it.url) || (it.content_type || "").startsWith("video");
    return kind === "video" ? v : !v;
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="media-picker">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-cantina-line">
          <h3 className="font-display font-bold text-lg text-cantina-ink">Media Library</h3>
          <button onClick={onClose} className="text-cantina-ink/50 hover:text-cantina-ink" data-testid="media-picker-close"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-cantina-pink" /></div>
          ) : shown.length === 0 ? (
            <p className="text-center text-sm text-cantina-ink/50 py-16">No {kind}s uploaded yet. Use the Upload button to add one.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {shown.map((it, i) => (
                <button key={i} onClick={() => { onSelect(it.url); onClose(); }}
                  className="group relative rounded-lg overflow-hidden border border-cantina-line aspect-square hover:ring-2 hover:ring-cantina-pink transition"
                  title={it.name} data-testid={`media-item-${i}`}>
                  {isVideo(it.url) || (it.content_type || "").startsWith("video")
                    ? <video src={resolveImg(it.url)} muted className="w-full h-full object-cover" />
                    : <img src={resolveImg(it.url)} alt={it.name} className="w-full h-full object-cover" />}
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition">{it.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, textarea, ...rest }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">{label}</span>
    {textarea ? (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2}
        className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm text-cantina-ink focus:outline-none focus:border-cantina-pink" {...rest} />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm text-cantina-ink focus:outline-none focus:border-cantina-pink" {...rest} />
    )}
  </label>
);

const ImageField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const url = await uploadImage(f); onChange(url); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div>
      <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">{label}</span>
      <div className="flex gap-3 items-center">
        <div className="w-20 h-20 rounded-lg overflow-hidden border border-cantina-line bg-cantina-alt shrink-0">
          {value ? <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="flex-1 space-y-2">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL"
            className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-xs text-cantina-ink focus:outline-none focus:border-cantina-pink" />
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-cantina-pink">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            <button type="button" onClick={() => setPick(true)} className="inline-flex items-center gap-2 text-xs font-semibold text-cantina-pink">
              <Images size={14} /> Library
            </button>
          </div>
        </div>
      </div>
      <MediaPicker open={pick} onClose={() => setPick(false)} onSelect={onChange} kind="image" />
    </div>
  );
};

const VideoField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const url = await uploadImage(f); onChange(url); toast.success("Video uploaded"); }
    catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div>
      <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">{label}</span>
      <div className="flex gap-3 items-center">
        <div className="w-28 h-20 rounded-lg overflow-hidden border border-cantina-line bg-cantina-alt shrink-0 flex items-center justify-center">
          {value ? <video src={resolveImg(value)} muted autoPlay loop playsInline className="w-full h-full object-cover" /> : <Film size={20} className="text-cantina-ink/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Video URL (e.g. /media/hero.mp4)"
            className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-xs text-cantina-ink focus:outline-none focus:border-cantina-pink" data-testid="admin-hero-video-url" />
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-cantina-pink">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
              <input type="file" accept="video/*" className="hidden" onChange={onFile} />
            </label>
            <button type="button" onClick={() => setPick(true)} className="inline-flex items-center gap-2 text-xs font-semibold text-cantina-pink">
              <Images size={14} /> Library
            </button>
            {value ? <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-cantina-chili">Remove</button> : null}
          </div>
        </div>
      </div>
      <MediaPicker open={pick} onClose={() => setPick(false)} onSelect={onChange} kind="video" />
    </div>
  );
};

const PdfField = ({ label, hint, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const url = await uploadImage(f); onChange(url); toast.success("PDF uploaded"); }
    catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <div>
      <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">{label}</span>
      {hint && <p className="text-xs text-cantina-ink/40 mb-2">{hint}</p>}
      <div className="flex flex-wrap gap-3 items-center">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/media/food-menu.pdf"
          className="flex-1 min-w-[200px] rounded-lg border border-cantina-line bg-white px-3 py-2 text-xs text-cantina-ink focus:outline-none focus:border-cantina-pink" />
        <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-semibold text-cantina-pink">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload PDF
          <input type="file" accept="application/pdf" className="hidden" onChange={onFile} />
        </label>
        {value && <a href={resolveImg(value)} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-cantina-pink">Preview ↗</a>}
      </div>
    </div>
  );
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const selCls = "w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm text-cantina-ink focus:outline-none focus:border-cantina-pink";
const Lbl = ({ children }) => <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">{children}</span>;

const genId = () => "p-" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const newPopup = () => ({
  id: genId(), enabled: true, page: "home", trigger: "load", delay: 1500, animation: "zoom",
  frequency: "once", image: "", title: "", subtitle: "", ctaLabel: "", ctaHref: "",
  schedule: { enabled: false, days: [], start: "11:00", end: "17:00" },
});

const PopupRow = ({ p, i, upd, onRemove }) => {
  const setF = (k, v) => upd(["popups", i, k], v);
  const setSched = (k, v) => upd(["popups", i, "schedule", k], v);
  const sched = p.schedule || {};
  const days = sched.days || [];
  const toggleDay = (d) => setSched("days", days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b));
  const sectionOpts = SECTION_OPTIONS[p.page] || [];
  return (
    <div className="border border-cantina-line rounded-2xl p-5 space-y-4 bg-cantina-alt/30" data-testid={`popup-row-${i}`}>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-bold text-cantina-ink cursor-pointer">
          <input type="checkbox" checked={p.enabled !== false} onChange={(e) => setF("enabled", e.target.checked)} data-testid={`popup-enabled-${i}`} />
          {p.title || "Untitled pop-up"}
        </label>
        <button onClick={() => onRemove(i)} className="text-cantina-chili flex items-center gap-1 text-sm shrink-0" data-testid={`popup-delete-${i}`}><Trash2 size={15} /> Delete</button>
      </div>

      <ImageField label="Flyer image" value={p.image} onChange={(v) => setF("image", v)} />
      <Field label="Title" value={p.title} onChange={(v) => setF("title", v)} data-testid={`popup-title-${i}`} />
      <Field label="Subtitle" value={p.subtitle} onChange={(v) => setF("subtitle", v)} textarea data-testid={`popup-subtitle-${i}`} />

      <div className="grid sm:grid-cols-2 gap-4">
        <div><Lbl>Page</Lbl><select className={selCls} value={p.page} onChange={(e) => setF("page", e.target.value)} data-testid={`popup-page-${i}`}>{PAGE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}</select></div>
        <div><Lbl>Entrance animation</Lbl><select className={selCls} value={p.animation} onChange={(e) => setF("animation", e.target.value)} data-testid={`popup-animation-${i}`}>{ANIMATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
        <div><Lbl>Trigger</Lbl><select className={selCls} value={p.trigger} onChange={(e) => setF("trigger", e.target.value)} data-testid={`popup-trigger-${i}`}><option value="load">On page load</option><option value="section">When scrolled to section</option></select></div>
        {p.trigger === "section" ? (
          <div><Lbl>Section</Lbl><select className={selCls} value={p.sectionId || ""} onChange={(e) => setF("sectionId", e.target.value)} data-testid={`popup-section-${i}`}><option value="">— choose —</option>{sectionOpts.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        ) : (
          <div><Lbl>Delay (ms)</Lbl><input type="number" min="300" step="100" className={selCls} value={p.delay || 1500} onChange={(e) => setF("delay", Number(e.target.value))} /></div>
        )}
        <div><Lbl>Frequency</Lbl><select className={selCls} value={p.frequency} onChange={(e) => setF("frequency", e.target.value)}><option value="once">Once per visit</option><option value="always">Every load</option></select></div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Button label (optional)" value={p.ctaLabel} onChange={(v) => setF("ctaLabel", v)} />
        <Field label="Button link (#section, /path or URL)" value={p.ctaHref} onChange={(v) => setF("ctaHref", v)} />
      </div>

      <div className="border-t border-cantina-line pt-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-cantina-ink cursor-pointer">
          <input type="checkbox" checked={!!sched.enabled} onChange={(e) => setSched("enabled", e.target.checked)} data-testid={`popup-schedule-enabled-${i}`} />
          Only show during a schedule (days &amp; time)
        </label>
        {sched.enabled && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {DAY_LABELS.map((d, di) => (
                <button key={di} onClick={() => toggleDay(di)} data-testid={`popup-day-${i}-${di}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${days.includes(di) ? "bg-cantina-pink text-white border-cantina-pink" : "bg-white text-cantina-ink border-cantina-line"}`}>{d}</button>
              ))}
            </div>
            <div className="flex gap-4 items-end">
              <div><Lbl>Start</Lbl><input type="time" className={selCls} value={sched.start || "11:00"} onChange={(e) => setSched("start", e.target.value)} data-testid={`popup-start-${i}`} /></div>
              <div><Lbl>End</Lbl><input type="time" className={selCls} value={sched.end || "17:00"} onChange={(e) => setSched("end", e.target.value)} data-testid={`popup-end-${i}`} /></div>
            </div>
            <p className="text-xs text-cantina-ink/40">Leave all days unselected to allow every day. Times use the visitor&apos;s local time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const ColorField = ({ ckey, label, value, def, onChange }) => {
  const val = value || def;
  const changed = val.toLowerCase() !== def.toLowerCase();
  return (
    <div className="flex items-center gap-3 border border-cantina-line rounded-xl p-3 bg-white" data-testid={`color-field-${ckey}`}>
      <input type="color" value={val} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg border border-cantina-line cursor-pointer p-0.5 shrink-0" data-testid={`color-input-${ckey}`} aria-label={label} />
      <div className="flex-1 min-w-0">
        <span className="block text-xs font-semibold text-cantina-ink truncate">{label}</span>
        <input value={val} onChange={(e) => onChange(e.target.value)} className="w-24 text-xs font-mono rounded border border-cantina-line px-2 py-1 mt-0.5 focus:outline-none focus:border-cantina-pink" data-testid={`color-hex-${ckey}`} />
      </div>
      {changed && <button onClick={() => onChange(def)} className="text-xs font-semibold text-cantina-pink shrink-0" data-testid={`color-reset-${ckey}`}>Reset</button>}
    </div>
  );
};

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.token);
      toast.success("Welcome back");
      onDone();
    } catch { toast.error("Invalid email or password"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cantina-cocoa px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-cantina-cream rounded-2xl p-8 shadow-2xl" data-testid="admin-login-form">
        <h1 className="font-display font-black text-3xl text-cantina-ink mb-1">Admin</h1>
        <p className="text-sm text-cantina-ink/60 mb-6">Pink Tequila content manager</p>
        <div className="space-y-4">
          <Field label="Email" value={email} onChange={setEmail} type="email" data-testid="admin-email" />
          <label className="block">
            <span className="block text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 mb-1.5">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-cantina-pink" data-testid="admin-password" />
          </label>
        </div>
        <button disabled={busy} className="btn-primary w-full mt-6 bg-cantina-pink text-cantina-cream font-semibold px-6 py-3 rounded-full flex items-center justify-center gap-2" data-testid="admin-login-submit">
          {busy && <Loader2 size={16} className="animate-spin" />} Sign In
        </button>
      </form>
    </div>
  );
};

const TABS = [
  { id: "info", label: "Basic Info" },
  { id: "branding", label: "Branding & Theme" },
  { id: "images", label: "Images" },
  { id: "menu", label: "Menu" },
  { id: "menus", label: "Menus & PDFs" },
  { id: "popups", label: "Pop-ups & Button" },
  { id: "text", label: "Homepage Text" },
  { id: "seo", label: "SEO" },
];

// --- Homepage Text editor ---
const TextRow = ({ f, val, onText, onSize, onWeight }) => {
  const v = val || {};
  return (
    <div className="border border-cantina-line rounded-xl p-4" data-testid={`text-row-${f.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-cantina-ink/50 pt-1">{f.label}</span>
        <div className="flex gap-2 shrink-0">
          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase text-cantina-ink/40">Size</span>
            <select value={v.size || "md"} onChange={(e) => onSize(e.target.value)}
              className="rounded-lg border border-cantina-line bg-white px-2 py-1.5 text-xs text-cantina-ink focus:outline-none focus:border-cantina-pink"
              data-testid={`text-size-${f.key}`}>
              {SIZE_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase text-cantina-ink/40">Bold</span>
            <select value={v.weight || "normal"} onChange={(e) => onWeight(e.target.value)}
              className="rounded-lg border border-cantina-line bg-white px-2 py-1.5 text-xs text-cantina-ink focus:outline-none focus:border-cantina-pink"
              data-testid={`text-weight-${f.key}`}>
              {WEIGHT_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        </div>
      </div>
      {f.typo ? (
        <p className="text-xs text-cantina-ink/40 italic">Style only — applies to every repeating item in this section.</p>
      ) : f.area ? (
        <textarea value={v.text || ""} onChange={(e) => onText(e.target.value)} rows={3}
          className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm text-cantina-ink focus:outline-none focus:border-cantina-pink"
          data-testid={`text-input-${f.key}`} />
      ) : (
        <input value={v.text || ""} onChange={(e) => onText(e.target.value)}
          className="w-full rounded-lg border border-cantina-line bg-white px-3 py-2 text-sm text-cantina-ink focus:outline-none focus:border-cantina-pink"
          data-testid={`text-input-${f.key}`} />
      )}
    </div>
  );
};

const ContentEditor = ({ id, s, upd }) => {
  const box = "border border-cantina-line rounded-xl p-4 bg-cantina-alt/40 space-y-3";
  const heading = (t) => <p className="text-xs font-bold uppercase tracking-wider text-cantina-pink mb-1">{t}</p>;

  if (id === "story") {
    const list = s.chapters || [];
    return (
      <div className="space-y-3 mb-4" data-testid="content-chapters">
        {heading("Story chapters (editable text)")}
        {list.map((c, i) => (
          <div key={i} className={box}>
            <div className="flex gap-3 items-end">
              <div className="w-20"><Field label="No." value={c.no} onChange={(v) => upd(["chapters", i, "no"], v)} /></div>
              <div className="flex-1"><Field label="Title" value={c.title} onChange={(v) => upd(["chapters", i, "title"], v)} data-testid={`chapter-title-${i}`} /></div>
              <button onClick={() => upd(["chapters"], list.filter((_, j) => j !== i))} className="mb-1 text-cantina-chili p-2" data-testid={`chapter-remove-${i}`}><Trash2 size={16} /></button>
            </div>
            <Field label="Body" value={c.body} onChange={(v) => upd(["chapters", i, "body"], v)} textarea data-testid={`chapter-body-${i}`} />
          </div>
        ))}
        <button onClick={() => upd(["chapters"], [...list, { no: String(list.length + 1).padStart(2, "0"), label: "", title: "New chapter", body: "" }])}
          className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="chapter-add"><Plus size={15} /> Add chapter</button>
      </div>
    );
  }

  if (id === "ribbon") {
    const list = s.ribbon || [];
    return (
      <div className="space-y-3 mb-4" data-testid="content-ribbon">
        {heading("Scrolling words (editable text)")}
        <div className="flex flex-wrap gap-2">
          {list.map((w, i) => (
            <div key={i} className="flex items-center gap-1 bg-white border border-cantina-line rounded-lg pl-2">
              <input value={w} onChange={(e) => { const n = [...list]; n[i] = e.target.value; upd(["ribbon"], n); }}
                className="w-32 px-1 py-1.5 text-sm text-cantina-ink focus:outline-none" data-testid={`ribbon-word-${i}`} />
              <button onClick={() => upd(["ribbon"], list.filter((_, j) => j !== i))} className="text-cantina-chili px-1.5" data-testid={`ribbon-remove-${i}`}><X size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={() => upd(["ribbon"], [...list, "New word"])} className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="ribbon-add"><Plus size={15} /> Add word</button>
      </div>
    );
  }

  if (id === "celebrate") {
    const list = s.celebrate || [];
    return (
      <div className="space-y-3 mb-4" data-testid="content-celebrate">
        {heading("Celebrate cards (editable text & images)")}
        {list.map((c, i) => (
          <div key={i} className={box}>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Tag" value={c.tag} onChange={(v) => upd(["celebrate", i, "tag"], v)} data-testid={`celebrate-tag-${i}`} />
              <Field label="Title" value={c.title} onChange={(v) => upd(["celebrate", i, "title"], v)} data-testid={`celebrate-title-${i}`} />
            </div>
            <Field label="Description" value={c.desc} onChange={(v) => upd(["celebrate", i, "desc"], v)} textarea data-testid={`celebrate-desc-${i}`} />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Button label" value={c.cta?.label} onChange={(v) => upd(["celebrate", i, "cta", "label"], v)} />
              <Field label="Button link" value={c.cta?.href} onChange={(v) => upd(["celebrate", i, "cta", "href"], v)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 items-start">
              <Field label="Accent color (hex)" value={c.color} onChange={(v) => upd(["celebrate", i, "color"], v)} />
              <ImageField label="Image" value={c.img} onChange={(v) => upd(["celebrate", i, "img"], v)} />
            </div>
            <button onClick={() => upd(["celebrate"], list.filter((_, j) => j !== i))} className="text-cantina-chili flex items-center gap-1 text-sm" data-testid={`celebrate-remove-${i}`}><Trash2 size={15} /> Remove card</button>
          </div>
        ))}
        <button onClick={() => upd(["celebrate"], [...list, { title: "New card", tag: "", desc: "", img: "", color: "#C9317D", cta: { label: "Learn more", href: "/menu" } }])}
          className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="celebrate-add"><Plus size={15} /> Add card</button>
      </div>
    );
  }

  if (id === "happyhour") {
    const hh = s.happyHour || {};
    const list = hh.specials || [];
    return (
      <div className="space-y-4 mb-4" data-testid="content-happyhour">
        {heading("Happy Hour (content, image & specials)")}
        <label className="flex items-center gap-2 text-sm font-semibold text-cantina-ink cursor-pointer">
          <input type="checkbox" checked={hh.enabled !== false} onChange={(e) => upd(["happyHour", "enabled"], e.target.checked)} data-testid="happyhour-enabled" />
          Show Happy Hour section (homepage + Bar Menu page)
        </label>
        <ImageField label="Specials poster image" value={hh.image} onChange={(v) => upd(["happyHour", "image"], v)} />
        <div className={box}>
          <p className="text-xs font-semibold uppercase tracking-wider text-cantina-ink/50">Specials list</p>
          {list.map((sp, i) => (
            <div key={i} className="flex gap-3 items-end">
              <div className="flex-1"><Field label="Name" value={sp.name} onChange={(v) => upd(["happyHour", "specials", i, "name"], v)} data-testid={`happyhour-name-${i}`} /></div>
              <div className="w-28"><Field label="Price" value={sp.price} onChange={(v) => upd(["happyHour", "specials", i, "price"], v)} data-testid={`happyhour-price-${i}`} /></div>
              <button onClick={() => upd(["happyHour", "specials"], list.filter((_, j) => j !== i))} className="mb-1 text-cantina-chili p-2" data-testid={`happyhour-remove-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
          <button onClick={() => upd(["happyHour", "specials"], [...list, { name: "New special", price: "$0.00" }])}
            className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="happyhour-add"><Plus size={15} /> Add special</button>
        </div>
      </div>
    );
  }

  return null;
};

const TextSection = ({ section, open, onToggle, textData, s, upd }) => (
  <section className="bg-white rounded-2xl border border-cantina-line overflow-hidden" data-testid={`text-section-${section.id}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between px-6 py-4 text-left" data-testid={`text-section-toggle-${section.id}`}>
      <h2 className="font-display font-bold text-lg text-cantina-ink">{section.label}</h2>
      <span className="text-cantina-ink/40 text-sm">{open ? "Hide −" : "Edit +"}</span>
    </button>
    {open && (
      <div className="px-6 pb-6 space-y-3">
        <ContentEditor id={section.id} s={s} upd={upd} />
        {section.fields.map((f) => (
          <TextRow
            key={f.key}
            f={f}
            val={(textData && textData[f.key]) || DEFAULT_TEXT[f.key]}
            onText={(v) => upd(["text", f.key, "text"], v)}
            onSize={(v) => upd(["text", f.key, "size"], v)}
            onWeight={(v) => upd(["text", f.key, "weight"], v)}
          />
        ))}
      </div>
    )}
  </section>
);

export default function Admin() {
  const { settings, menu, refresh } = useContent();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("info");
  const [openSec, setOpenSec] = useState("hero");
  const [s, setS] = useState(null);
  const [m, setM] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      if (getToken()) {
        try { await api.get("/auth/me"); setAuthed(true); } catch { clearToken(); }
      }
      setChecking(false);
    })();
  }, []);

  useEffect(() => {
    if (authed) {
      setS(clone({ ...DEFAULT_SETTINGS, ...settings, images: { ...DEFAULT_SETTINGS.images, ...settings.images }, menus: { ...DEFAULT_SETTINGS.menus, ...(settings.menus || {}) }, happyHour: { ...DEFAULT_SETTINGS.happyHour, ...(settings.happyHour || {}) }, orderFab: { ...DEFAULT_SETTINGS.orderFab, ...(settings.orderFab || {}) }, theme: { ...DEFAULT_THEME, ...(settings.theme || {}) }, popups: settings.popups || DEFAULT_SETTINGS.popups, topPicks: { ...DEFAULT_SETTINGS.topPicks, ...(settings.topPicks || {}) }, text: { ...DEFAULT_TEXT, ...(settings.text || {}) } }));
      setM(clone(menu && menu.length ? menu : DEFAULT_MENU));
    }
  }, [authed, settings, menu]);

  // Live-preview the theme colors while editing; restore saved theme on unmount.
  useEffect(() => {
    if (s?.theme) applyTheme(s.theme);
  }, [s?.theme]);
  useEffect(() => () => applyTheme(settings.theme || {}), []);

  if (checking) return <div className="min-h-screen bg-cantina-cocoa flex items-center justify-center"><Loader2 className="animate-spin text-cantina-cream" /></div>;
  if (!authed) return <Login onDone={() => setAuthed(true)} />;
  if (!s || !m) return null;

  const saveSettings = async (msg = "Saved") => {
    setSaving(true);
    try { await api.put("/content/settings", { data: s }); await refresh(); toast.success(msg); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };
  const saveMenu = async () => {
    setSaving(true);
    try { await api.put("/content/menu", { data: m }); await refresh(); toast.success("Menu saved"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const upd = (path, value) => {
    setS((prev) => { const n = clone(prev); let o = n; for (let i = 0; i < path.length - 1; i++) o = o[path[i]]; o[path[path.length - 1]] = value; return n; });
  };

  return (
    <div className="min-h-screen bg-cantina-alt" data-testid="admin-panel">
      <Seo noindex title="Admin · Pink Tequila" path="/admin" />
      <header className="bg-cantina-cocoa text-cantina-cream sticky top-0 z-20">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-display font-black text-xl">Pink Tequila · Admin</span>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-cantina-cream/70 hover:text-cantina-cream">View site ↗</a>
            <button onClick={() => { clearToken(); setAuthed(false); }} className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-[1100px] mx-auto px-6 flex gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? "border-cantina-marigold text-cantina-cream" : "border-transparent text-cantina-cream/50 hover:text-cantina-cream"}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 py-8">
        {tab === "info" && (
          <div className="space-y-8">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Contact</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Phone" value={s.phone} onChange={(v) => upd(["phone"], v)} data-testid="admin-phone" />
                <Field label="Address" value={s.address} onChange={(v) => upd(["address"], v)} data-testid="admin-address" />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Hours</h2>
              <div className="space-y-3">
                {s.hours.map((h, i) => (
                  <div key={i} className="flex gap-3 items-end">
                    <div className="flex-1"><Field label="Day" value={h.day} onChange={(v) => upd(["hours", i, "day"], v)} /></div>
                    <div className="flex-1"><Field label="Time" value={h.time} onChange={(v) => upd(["hours", i, "time"], v)} /></div>
                    <button onClick={() => upd(["hours"], s.hours.filter((_, j) => j !== i))} className="mb-1 text-cantina-chili p-2"><Trash2 size={16} /></button>
                  </div>
                ))}
                <button onClick={() => upd(["hours"], [...s.hours, { day: "", time: "" }])} className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink"><Plus size={15} /> Add row</button>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Testimonials</h2>
              <div className="space-y-4">
                {s.testimonials.map((t, i) => (
                  <div key={i} className="border border-cantina-line rounded-xl p-4 space-y-3">
                    <Field label="Quote" value={t.quote} onChange={(v) => upd(["testimonials", i, "quote"], v)} textarea />
                    <div className="flex gap-3 items-end">
                      <div className="flex-1"><Field label="Author" value={t.by} onChange={(v) => upd(["testimonials", i, "by"], v)} /></div>
                      <button onClick={() => upd(["testimonials"], s.testimonials.filter((_, j) => j !== i))} className="mb-1 text-cantina-chili p-2"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => upd(["testimonials"], [...s.testimonials, { quote: "", by: "" }])} className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink"><Plus size={15} /> Add testimonial</button>
              </div>
            </section>
            <SaveBar saving={saving} onSave={() => saveSettings("Info saved")} label="Save Info" testid="admin-save-info" />
          </div>
        )}

        {tab === "branding" && (
          <div className="space-y-8" data-testid="admin-branding">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Logo</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">Replace the restaurant logo (used in the navbar &amp; footer). A transparent PNG works best.</p>
              <div className="max-w-md rounded-xl p-5 bg-cantina-cocoadeep mb-4 flex items-center justify-center">
                <img src={resolveImg(s.images?.logo) || "/brand/logo.png"} alt="Logo preview" className="h-20 w-auto object-contain" data-testid="branding-logo-preview" />
              </div>
              <ImageField label="Logo image" value={s.images?.logo} onChange={(v) => upd(["images", "logo"], v)} />
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6" data-testid="admin-theme">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h2 className="font-display font-bold text-xl text-cantina-ink">Color Scheme</h2>
                <button onClick={() => upd(["theme"], { ...DEFAULT_THEME })} className="text-sm font-semibold text-cantina-pink" data-testid="theme-reset-all">Reset all to default</button>
              </div>
              <p className="text-sm text-cantina-ink/50 mb-5">Recolor the entire site for a sister restaurant. Changes preview live as you pick — click <strong>Save Branding</strong> to keep them.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {THEME_COLORS.map((c) => (
                  <ColorField key={c.key} ckey={c.key} label={c.label} def={c.d} value={(s.theme || {})[c.key]} onChange={(v) => upd(["theme", c.key], v)} />
                ))}
              </div>
              <h3 className="font-display font-bold text-base text-cantina-ink mt-7 mb-3">Scrollbar &amp; Selection</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {CHROME_COLORS.map((c) => (
                  <ColorField key={c.key} ckey={c.key} label={c.label} def={c.d} value={(s.theme || {})[c.key]} onChange={(v) => upd(["theme", c.key], v)} />
                ))}
              </div>
            </section>
            <SaveBar saving={saving} onSave={() => saveSettings("Branding saved")} label="Save Branding" testid="admin-save-branding" />
          </div>
        )}

        {tab === "images" && (
          <div className="space-y-8">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Hero (Homepage Top)</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">The looping background video plays at the top of the site. The poster image shows while the video loads (and on slow connections). Leave the video empty to use the poster image only.</p>
              <div className="grid md:grid-cols-2 gap-6">
                <VideoField label="Hero — Background Video" value={s.images.heroVideo} onChange={(v) => upd(["images", "heroVideo"], v)} />
                <ImageField label="Hero — Poster / Fallback Image" value={s.images.hero} onChange={(v) => upd(["images", "hero"], v)} />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Section Images</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <ImageField label="Story — Main" value={s.images.storyMain} onChange={(v) => upd(["images", "storyMain"], v)} />
                <ImageField label="Story — Secondary" value={s.images.storySecond} onChange={(v) => upd(["images", "storySecond"], v)} />
                <ImageField label="Cultura (Nuestra cultura)" value={s.images.cultura} onChange={(v) => upd(["images", "cultura"], v)} />
                <ImageField label="Visit" value={s.images.visit} onChange={(v) => upd(["images", "visit"], v)} />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Signature Cards (Home)</h2>
              <div className="space-y-5">
                {s.signatures.map((sig, i) => (
                  <div key={i} className="border border-cantina-line rounded-xl p-4 grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Field label="Tag" value={sig.kicker} onChange={(v) => upd(["signatures", i, "kicker"], v)} />
                      <Field label="Name" value={sig.name} onChange={(v) => upd(["signatures", i, "name"], v)} />
                      <Field label="Description" value={sig.desc} onChange={(v) => upd(["signatures", i, "desc"], v)} textarea />
                      <Field label="Accent color (hex)" value={sig.color} onChange={(v) => upd(["signatures", i, "color"], v)} />
                    </div>
                    <ImageField label="Image" value={sig.img} onChange={(v) => upd(["signatures", i, "img"], v)} />
                  </div>
                ))}
              </div>
            </section>
            <section className="bg-white rounded-2xl border border-cantina-line p-6" data-testid="admin-gallery-editor">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-xl text-cantina-ink">Photo Gallery</h2>
                <span className="text-xs text-cantina-ink/50">{(s.gallery || []).length} photos</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {(s.gallery || []).map((g, i) => (
                  <div key={i} className="border border-cantina-line rounded-xl p-4 space-y-3">
                    <ImageField label={`Photo ${i + 1}`} value={g.img} onChange={(v) => upd(["gallery", i, "img"], v)} />
                    <div className="flex gap-2 items-end">
                      <div className="flex-1"><Field label="Caption" value={g.name} onChange={(v) => upd(["gallery", i, "name"], v)} /></div>
                      <button onClick={() => upd(["gallery"], s.gallery.filter((_, j) => j !== i))} className="mb-1 text-cantina-chili p-2" data-testid={`gallery-remove-${i}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => upd(["gallery"], [...(s.gallery || []), { img: "", name: "New photo" }])}
                className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="gallery-add"><Plus size={15} /> Add photo</button>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6" data-testid="admin-toppicks-editor">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Homepage Menu Top Picks</h2>
              <p className="text-sm text-cantina-ink/50 mb-5">The &ldquo;Menu top picks&rdquo; cards on the homepage, split into the <strong>Top Eats</strong> and <strong>Top Drinks</strong> tabs.</p>
              {[["eats", "Top Eats"], ["drinks", "Top Drinks"]].map(([key, label]) => {
                const list = (s.topPicks && s.topPicks[key]) || [];
                return (
                  <div key={key} className="mb-8 last:mb-0" data-testid={`toppicks-${key}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold text-lg text-cantina-ink">{label}</h3>
                      <span className="text-xs text-cantina-ink/50">{list.length} items</span>
                    </div>
                    <div className="space-y-4">
                      {list.map((it, i) => (
                        <div key={i} className="border border-cantina-line rounded-xl p-4 grid md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <Field label="Name" value={it.name} onChange={(v) => upd(["topPicks", key, i, "name"], v)} data-testid={`toppicks-${key}-name-${i}`} />
                            <Field label="Price" value={it.price} onChange={(v) => upd(["topPicks", key, i, "price"], v)} />
                            <Field label="Description" value={it.desc} onChange={(v) => upd(["topPicks", key, i, "desc"], v)} textarea />
                            <button onClick={() => upd(["topPicks", key], list.filter((_, j) => j !== i))} className="text-cantina-chili flex items-center gap-1 text-sm" data-testid={`toppicks-${key}-remove-${i}`}><Trash2 size={15} /> Remove</button>
                          </div>
                          <ImageField label="Image" value={it.img} onChange={(v) => upd(["topPicks", key, i, "img"], v)} />
                        </div>
                      ))}
                    </div>
                    <button onClick={() => upd(["topPicks", key], [...list, { name: "New item", price: "$0.00", desc: "", img: "" }])}
                      className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid={`toppicks-${key}-add`}><Plus size={15} /> Add {label} item</button>
                  </div>
                );
              })}
            </section>
            <SaveBar saving={saving} onSave={() => saveSettings("Images saved")} label="Save Images" testid="admin-save-images" />
          </div>
        )}

        {tab === "menu" && (
          <div className="space-y-6">
            {m.map((cat, ci) => (
              <section key={ci} className="bg-white rounded-2xl border border-cantina-line p-6" data-testid={`admin-cat-${ci}`}>
                <div className="flex items-center gap-3 mb-4">
                  <input value={cat.name} onChange={(e) => setM((p) => { const n = clone(p); n[ci].name = e.target.value; return n; })}
                    className="font-display font-bold text-xl text-cantina-ink border-b border-cantina-line focus:outline-none focus:border-cantina-pink flex-1" />
                  <button onClick={() => setM((p) => p.filter((_, j) => j !== ci))} className="text-cantina-chili flex items-center gap-1 text-sm"><Trash2 size={15} /> Category</button>
                </div>
                <div className="space-y-4">
                  {cat.items.map((it, ii) => (
                    <div key={ii} className="grid md:grid-cols-[1fr_auto] gap-4 border border-cantina-line rounded-xl p-4">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Name" value={it.name} onChange={(v) => setM((p) => { const n = clone(p); n[ci].items[ii].name = v; return n; })} />
                        <Field label="Price" value={it.price} onChange={(v) => setM((p) => { const n = clone(p); n[ci].items[ii].price = v; return n; })} />
                        <div className="sm:col-span-2"><Field label="Description" value={it.desc} onChange={(v) => setM((p) => { const n = clone(p); n[ci].items[ii].desc = v; return n; })} textarea /></div>
                        {cat.id !== "sides" && <div className="sm:col-span-2"><ImageField label="Image" value={it.img} onChange={(v) => setM((p) => { const n = clone(p); n[ci].items[ii].img = v; return n; })} /></div>}
                      </div>
                      <button onClick={() => setM((p) => { const n = clone(p); n[ci].items = n[ci].items.filter((_, j) => j !== ii); return n; })} className="text-cantina-chili self-start p-2"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => setM((p) => { const n = clone(p); n[ci].items.push({ name: "New item", price: "$0.00", desc: "", img: "" }); return n; })}
                    className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink"><Plus size={15} /> Add item</button>
                </div>
              </section>
            ))}
            <button onClick={() => setM((p) => [...p, { id: `cat-${Date.now()}`, name: "New Category", items: [] }])}
              className="flex items-center gap-1.5 text-sm font-semibold text-cantina-pink"><Plus size={15} /> Add category</button>
            <SaveBar saving={saving} onSave={saveMenu} label="Save Menu" testid="admin-save-menu" />
          </div>
        )}

        {tab === "menus" && (
          <div className="space-y-8" data-testid="admin-menus">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Downloadable Menus (PDF)</h2>
              <p className="text-sm text-cantina-ink/50 mb-5">These PDFs power the &ldquo;Download Menu&rdquo; buttons. Upload a new PDF to replace them anytime.</p>
              <div className="space-y-5">
                <PdfField label="Food Menu PDF" hint="Shown as the Download button on the Menu page." value={s.menus?.foodPdf} onChange={(v) => upd(["menus", "foodPdf"], v)} />
                <PdfField label="Drink Menu PDF" hint="Downloadable from the Bar Menu (flipbook) page." value={s.menus?.drinksPdf} onChange={(v) => upd(["menus", "drinksPdf"], v)} />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6" data-testid="admin-drinks-pages">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display font-bold text-xl text-cantina-ink">Bar Menu Flipbook Pages</h2>
                <span className="text-xs text-cantina-ink/50">{(s.menus?.drinksPages || []).length} pages</span>
              </div>
              <p className="text-sm text-cantina-ink/50 mb-5">The interactive drink book on the <strong>/drinks</strong> page. Pages appear in the order below — use the arrows to reorder. Tall portrait images work best.</p>
              <div className="space-y-4">
                {(s.menus?.drinksPages || []).map((p, i) => {
                  const list = s.menus.drinksPages;
                  const move = (from, to) => {
                    if (to < 0 || to >= list.length) return;
                    const n = [...list]; const [x] = n.splice(from, 1); n.splice(to, 0, x); upd(["menus", "drinksPages"], n);
                  };
                  return (
                    <div key={i} className="border border-cantina-line rounded-xl p-4 flex gap-4 items-start">
                      <span className="font-display font-bold text-cantina-ink/40 pt-1 w-6 shrink-0">{i + 1}</span>
                      <div className="flex-1"><ImageField label={`Page ${i + 1}`} value={p} onChange={(v) => { const n = [...list]; n[i] = v; upd(["menus", "drinksPages"], n); }} /></div>
                      <div className="flex flex-col gap-1 pt-1">
                        <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-cantina-pink disabled:opacity-25 text-xs font-bold px-2 py-1" data-testid={`drinks-page-up-${i}`}>↑</button>
                        <button onClick={() => move(i, i + 1)} disabled={i === list.length - 1} className="text-cantina-pink disabled:opacity-25 text-xs font-bold px-2 py-1" data-testid={`drinks-page-down-${i}`}>↓</button>
                        <button onClick={() => upd(["menus", "drinksPages"], list.filter((_, j) => j !== i))} className="text-cantina-chili px-2 py-1" data-testid={`drinks-page-remove-${i}`}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button onClick={() => upd(["menus", "drinksPages"], [...(s.menus?.drinksPages || []), ""])}
                className="mt-4 flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="drinks-page-add"><Plus size={15} /> Add page</button>
            </section>
            <SaveBar saving={saving} onSave={() => saveSettings("Menus saved")} label="Save Menus" testid="admin-save-menus" />
          </div>
        )}

        {tab === "popups" && (
          <div className="space-y-6" data-testid="admin-popups">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Floating &ldquo;Order Online&rdquo; Button</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">A fancy animated button that stays fixed on-screen on every page (works on desktop &amp; iPhone).</p>
              <label className="flex items-center gap-2 text-sm font-semibold text-cantina-ink cursor-pointer">
                <input type="checkbox" checked={s.orderFab?.enabled !== false} onChange={(e) => upd(["orderFab", "enabled"], e.target.checked)} data-testid="orderfab-enabled" />
                Show the floating Order Online button
              </label>
              <p className="text-xs text-cantina-ink/40 mt-2">Its label &amp; link come from the Order Online settings (Navbar text &amp; Basic Info order URL).</p>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6" data-testid="admin-popups-list">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display font-bold text-xl text-cantina-ink">Pop-up Flyer Ads</h2>
                <span className="text-xs text-cantina-ink/50">{(s.popups || []).length} ad(s)</span>
              </div>
              <p className="text-sm text-cantina-ink/50 mb-5">Show a flyer to visitors — pick the page, when it appears (on load or when a section scrolls into view), the entrance animation, and an optional schedule (e.g. only during Happy Hour). Each ad shows once per visit unless set to every load.</p>
              <div className="space-y-5">
                {(s.popups || []).map((p, i) => (
                  <PopupRow key={p.id || i} p={p} i={i} upd={upd} onRemove={(idx) => upd(["popups"], s.popups.filter((_, j) => j !== idx))} />
                ))}
              </div>
              <button onClick={() => upd(["popups"], [...(s.popups || []), newPopup()])}
                className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-cantina-pink" data-testid="popup-add"><Plus size={15} /> Add pop-up ad</button>
            </section>
            <SaveBar saving={saving} onSave={() => saveSettings("Pop-ups saved")} label="Save Pop-ups" testid="admin-save-popups" />
          </div>
        )}

        {tab === "text" && (
          <div className="space-y-4" data-testid="admin-text">
            <div className="bg-cantina-pink/5 border border-cantina-pink/20 rounded-2xl p-5">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Homepage Text & Typography</h2>
              <p className="text-sm text-cantina-ink/60">Edit every piece of copy on the homepage. Use the <strong>Size</strong> and <strong>Bold</strong> dropdowns next to each line to control how big and heavy it appears (sizes scale automatically on mobile). Rows marked &ldquo;style only&rdquo; set the look of repeating items like menu cards.</p>
            </div>
            {TEXT_SCHEMA.map((section) => (
              <TextSection
                key={section.id}
                section={section}
                open={openSec === section.id}
                onToggle={() => setOpenSec(openSec === section.id ? null : section.id)}
                textData={s.text}
                s={s}
                upd={upd}
              />
            ))}
            <SaveBar saving={saving} onSave={() => saveSettings("Text saved")} label="Save Text" testid="admin-save-text" />
          </div>
        )}

        {tab === "seo" && (
          <div className="space-y-8" data-testid="admin-seo">
            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">General / Homepage</h2>
              <p className="text-sm text-cantina-ink/50 mb-5">Controls how the site appears on Google and when links are shared. Keep titles under ~60 characters and descriptions under ~160.</p>
              <div className="space-y-4">
                <Field label="Site Name" value={s.seo?.siteName} onChange={(v) => upd(["seo", "siteName"], v)} placeholder="Pink Tequila" />
                <Field label="Homepage Title" value={s.seo?.title} onChange={(v) => upd(["seo", "title"], v)} data-testid="seo-title" />
                <Field label="Homepage Meta Description" value={s.seo?.description} onChange={(v) => upd(["seo", "description"], v)} textarea data-testid="seo-description" />
                <Field label="Keywords (comma separated)" value={s.seo?.keywords} onChange={(v) => upd(["seo", "keywords"], v)} textarea />
                <Field label="Canonical Base URL (your live domain)" value={s.seo?.canonicalBase} onChange={(v) => upd(["seo", "canonicalBase"], v)} placeholder="https://www.pink-tequila.com" />
                <label className="flex items-center gap-2 text-sm font-semibold text-cantina-ink cursor-pointer">
                  <input type="checkbox" checked={s.seo?.searchVisible !== false} onChange={(e) => upd(["seo", "searchVisible"], e.target.checked)} data-testid="seo-searchvisible" />
                  Allow search engines to index this site
                </label>
                <p className="text-xs text-cantina-ink/40 -mt-2">Uncheck only for staging. When off, the whole site is set to noindex, nofollow.</p>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Social Sharing (Open Graph &amp; Twitter/X)</h2>
              <div className="space-y-4">
                <ImageField label="Social Share Image (1200×630 recommended)" value={s.seo?.ogImage} onChange={(v) => upd(["seo", "ogImage"], v)} />
                <Field label="Share Title (optional — defaults to page title)" value={s.seo?.ogTitle} onChange={(v) => upd(["seo", "ogTitle"], v)} />
                <Field label="Share Description (optional)" value={s.seo?.ogDescription} onChange={(v) => upd(["seo", "ogDescription"], v)} textarea />
                <Field label="Twitter / X handle (@yourhandle)" value={s.seo?.twitterHandle} onChange={(v) => upd(["seo", "twitterHandle"], v)} placeholder="@pinktequilafl" />
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-cantina-ink/50">Browser theme color</span>
                  <input type="color" value={s.seo?.themeColor || "#170E12"} onChange={(e) => upd(["seo", "themeColor"], e.target.value)} className="w-10 h-9 rounded-lg border border-cantina-line cursor-pointer p-0.5" data-testid="seo-themecolor" />
                </div>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-4">Per-Page Titles &amp; Descriptions</h2>
              <div className="space-y-4">
                <Field label="Full Menu — Title" value={s.seo?.menuTitle} onChange={(v) => upd(["seo", "menuTitle"], v)} />
                <Field label="Full Menu — Description" value={s.seo?.menuDescription} onChange={(v) => upd(["seo", "menuDescription"], v)} textarea />
                <Field label="Bar Menu — Title" value={s.seo?.drinksTitle} onChange={(v) => upd(["seo", "drinksTitle"], v)} />
                <Field label="Bar Menu — Description" value={s.seo?.drinksDescription} onChange={(v) => upd(["seo", "drinksDescription"], v)} textarea />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Business Info &amp; Rich Results (Restaurant schema)</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">Powers Google&apos;s local rich results. Address, phone &amp; hours come from the Basic Info tab.</p>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <Field label="Business Name" value={s.seo?.businessName} onChange={(v) => upd(["seo", "businessName"], v)} />
                <Field label="Price Range" value={s.seo?.priceRange} onChange={(v) => upd(["seo", "priceRange"], v)} placeholder="$$" />
                <Field label="Cuisine" value={s.seo?.cuisine} onChange={(v) => upd(["seo", "cuisine"], v)} placeholder="Mexican" />
                <Field label="Latitude" value={s.seo?.geoLat} onChange={(v) => upd(["seo", "geoLat"], v)} placeholder="27.0442" />
                <Field label="Longitude" value={s.seo?.geoLng} onChange={(v) => upd(["seo", "geoLng"], v)} placeholder="-82.2359" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-cantina-ink cursor-pointer">
                <input type="checkbox" checked={!!s.seo?.acceptsReservations} onChange={(e) => upd(["seo", "acceptsReservations"], e.target.checked)} data-testid="seo-reservations" />
                Accepts reservations
              </label>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Social Profiles (sameAs)</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">Linked in structured data so Google connects your official profiles. Facebook comes from your brand settings.</p>
              <div className="space-y-4">
                <Field label="Instagram URL" value={s.seo?.instagram} onChange={(v) => upd(["seo", "instagram"], v)} placeholder="https://instagram.com/..." />
                <Field label="Twitter / X URL" value={s.seo?.xUrl} onChange={(v) => upd(["seo", "xUrl"], v)} placeholder="https://x.com/..." />
                <Field label="Yelp URL" value={s.seo?.yelp} onChange={(v) => upd(["seo", "yelp"], v)} placeholder="https://yelp.com/biz/..." />
                <Field label="TripAdvisor URL" value={s.seo?.tripadvisor} onChange={(v) => upd(["seo", "tripadvisor"], v)} />
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-cantina-line p-6">
              <h2 className="font-display font-bold text-xl text-cantina-ink mb-1">Search Console Verification</h2>
              <p className="text-sm text-cantina-ink/50 mb-4">Paste the verification codes to prove ownership (adds the meta tags for you).</p>
              <div className="space-y-4">
                <Field label="Google Search Console code" value={s.seo?.googleVerification} onChange={(v) => upd(["seo", "googleVerification"], v)} />
                <Field label="Bing Webmaster code" value={s.seo?.bingVerification} onChange={(v) => upd(["seo", "bingVerification"], v)} />
              </div>
            </section>

            <SaveBar saving={saving} onSave={() => saveSettings("SEO saved")} label="Save SEO" testid="admin-save-seo" />
          </div>
        )}
      </main>
    </div>
  );
}

const SaveBar = ({ saving, onSave, label, testid }) => (
  <div className="sticky bottom-4 flex justify-end">
    <button onClick={onSave} disabled={saving}
      className="btn-primary bg-cantina-pink text-cantina-cream font-semibold px-8 py-3 rounded-full flex items-center gap-2 shadow-xl" data-testid={testid}>
      {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {label}
    </button>
  </div>
);
